<template>
  <div class="container-history-main">
    <!-- Header Section -->
    <VCard class="mb-6 container-history-header">
      <VCardTitle class="d-flex align-center justify-space-between">
        <div class="d-flex align-center container-title">
          <VIcon
            icon="tabler-truck-delivery"
            class="me-3"
            size="28"
            color="primary"
          />
          <div>
            <h2 class="text-h5 mb-0">
              {{ $t('history.containers.title') }}
            </h2>
            <p class="text-subtitle-2 mb-0 text-medium-emphasis">
              {{ $t('history.containers.subtitle') }}
            </p>
          </div>
        </div>
        
        <div class="d-flex align-center gap-3">
          <!-- View Toggle -->
          <VBtnToggle
            v-model="viewMode"
            mandatory
            variant="outlined"
            divided
            density="compact"
            class="view-toggle"
          >
            <VBtn
              value="table"
              icon="tabler-table"
              size="small"
            />
            <VBtn
              value="card"
              icon="tabler-layout-grid"
              size="small"
            />
          </VBtnToggle>
          
          <VBtn
            color="primary"
            variant="outlined"
            prepend-icon="tabler-download"
            class="export-containers-btn"
            @click="exportData"
          >
            {{ $t('history.containers.export_container_data') }}
          </VBtn>
        </div>
      </VCardTitle>
        
      <!-- Filters Section -->
      <VCardText>
        <VRow class="container-filters">
          <VCol
            cols="12"
            md="3"
          >
            <VTextField
              v-model="searchQuery"
              :label="$t('history.containers.search_container')"
              prepend-inner-icon="tabler-search"
              density="compact"
              hide-details
              clearable
              variant="outlined"
              class="container-search-field"
              @keyup.enter="fetchContainers"
            />
          </VCol>
            
          <VCol
            cols="12"
            md="2"
          >
            <VSelect
              v-model="selectedYear"
              :items="yearOptions"
              :label="$t('history.containers.filter_year')"
              item-title="title"
              item-value="value"
              density="compact"
              hide-details
              variant="outlined"
              class="year-filter"
              @update:model-value="fetchContainers"
            />
          </VCol>
            
          <VCol
            cols="12"
            md="2"
          >
            <VSelect
              v-model="selectedType"
              :items="typeOptions"
              :label="$t('history.containers.filter_type')"
              item-title="title"
              item-value="value"
              density="compact"
              hide-details
              variant="outlined"
              class="type-filter"
              @update:model-value="fetchContainers"
            />
          </VCol>
            
          <VCol
            cols="12"
            md="3"
          >
            <VSelect
              v-model="selectedOrigin"
              :items="originOptions"
              :label="$t('history.containers.filter_origin')"
              item-title="title"
              item-value="value"
              density="compact"
              hide-details
              variant="outlined"
              class="origin-filter"
              @update:model-value="fetchContainers"
            />
          </VCol>
            
          <VCol
            cols="12"
            md="2"
            class="d-flex align-center"
          >
            <VBtn 
              color="primary" 
              class="apply-filters-btn"
              :loading="isLoading"
              @click="fetchContainers"
            >
              {{ $t('history.filters.apply_filters') }}
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>
  
    <!-- Container History Content -->
    <VCard class="container-history-content">
      <VCardText>
        <div
          v-if="isLoading"
          class="text-center py-8"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </div>
          
        <div
          v-else-if="containers.length === 0"
          class="text-center py-8 no-data-section"
        >
          <VIcon
            icon="tabler-truck-off"
            size="64"
            class="mb-4 text-disabled"
          />
          <h3 class="text-h6 mb-2">
            {{ $t('history.containers.no_history') }}
          </h3>
        </div>
          
        <!-- Table View -->
        <div v-else-if="viewMode === 'table'">
          <VDataTable
            :items="containers"
            :headers="tableHeaders"
            :loading="isLoading"
            class="container-history-table"
            :items-per-page="itemsPerPage"
            :page="currentPage"
            hide-default-footer
          >
            <template #item.container_number="{ item }">
              <div class="d-flex align-center">
                <VAvatar
                  :color="item.transport_type === 'air' ? 'info' : 'primary'"
                  variant="tonal"
                  size="32"
                  class="me-3"
                >
                  <VIcon
                    :icon="item.transport_type === 'air' ? 'tabler-plane' : 'tabler-ship'"
                    size="16"
                  />
                </VAvatar>
                <div>
                  <div class="font-weight-medium">
                    {{ item.container_number }}
                  </div>
                  <VChip
                    :color="item.transport_type === 'air' ? 'info' : 'primary'"
                    size="x-small"
                    class="mt-1"
                  >
                    {{ item.transport_type === 'air' ? $t('shipment.by_plane') : $t('shipment.by_ship') }}
                  </VChip>
                </div>
              </div>
            </template>
            
            <template #item.parcels_count="{ item }">
              <VChip
                color="primary"
                size="small"
              >
                {{ item.parcels_count || 0 }}
              </VChip>
            </template>
            
            <template #item.clients_count="{ item }">
              <VChip
                color="secondary"
                size="small"
              >
                {{ item.clients_count || 0 }}
              </VChip>
            </template>
            
            <template #item.total_weight="{ item }">
              <div class="d-flex align-center">
                <VIcon
                  icon="tabler-weight"
                  size="16"
                  class="me-1"
                />
                <span class="font-weight-bold">{{ formatWeight(item.total_weight || 0) }}</span>
              </div>
            </template>
            
            <template #item.total_volume="{ item }">
              <div class="d-flex align-center">
                <VIcon
                  icon="tabler-box"
                  size="16"
                  class="me-1"
                />
                <span class="font-weight-bold">{{ formatVolume(item.total_volume || 0) }}</span>
              </div>
            </template>
            
            <template #item.processing_days="{ item }">
              <VChip
                :color="getProcessingColor(item.processing_days)"
                size="small"
              >
                {{ item.processing_days || 0 }} {{ $t('history.containers.days') }}
              </VChip>
            </template>
            
            <template #item.arrival_date="{ item }">
              {{ formatDate(item.arrival_date) }}
            </template>
            
            <template #item.completed_date="{ item }">
              {{ formatDate(item.completed_date) }}
            </template>
            
            <template #item.actions="{ item }">
              <div class="d-flex gap-1">
                <VBtn
                  icon
                  variant="text"
                  size="small"
                  color="primary"
                  :to="`/history/containers/${item.id}`"
                >
                  <VIcon icon="tabler-eye" />
                </VBtn>
                <VBtn
                  icon
                  variant="text"
                  size="small"
                  color="secondary"
                  @click="generateContainerReport(item)"
                >
                  <VIcon icon="tabler-file-analytics" />
                </VBtn>
              </div>
            </template>
          </VDataTable>
        </div>
        
        <!-- Card View -->
        <div v-else>
          <VRow>
            <VCol 
              v-for="container in containers" 
              :key="container.id" 
              cols="12" 
              lg="6"
              xl="4"
            >
              <VCard 
                variant="outlined" 
                class="container-card"
                hover
                :to="`/history/containers/${container.id}`"
              >
                <VCardText class="pa-4">
                  <!-- Container Header -->
                  <div class="d-flex align-center mb-3">
                    <VAvatar
                      :color="container.transport_type === 'air' ? 'info' : 'primary'"
                      variant="tonal"
                      size="40"
                      class="me-3"
                    >
                      <VIcon :icon="container.transport_type === 'air' ? 'tabler-plane' : 'tabler-ship'" />
                    </VAvatar>
                    <div class="flex-grow-1">
                      <h5 class="text-subtitle-1 mb-1">
                        {{ container.container_number }}
                      </h5>
                      <div class="d-flex align-center gap-2">
                        <VChip
                          :color="container.transport_type === 'air' ? 'info' : 'primary'"
                          size="x-small"
                        >
                          {{ container.transport_type === 'air' ? $t('shipment.by_plane') : $t('shipment.by_ship') }}
                        </VChip>
                        <VChip
                          color="success"
                          size="x-small"
                        >
                          {{ $t('shipment.statuses.completed') }}
                        </VChip>
                      </div>
                    </div>
                  </div>
                    
                  <!-- Dates -->
                  <VRow
                    dense
                    class="mb-3 container-dates"
                  >
                    <VCol cols="6">
                      <div class="text-center">
                        <div class="text-caption text-disabled">
                          {{ $t('history.containers.arrived_date') }}
                        </div>
                        <div class="text-subtitle-2 text-info">
                          {{ formatDate(container.arrival_date) }}
                        </div>
                      </div>
                    </VCol>
                    <VCol cols="6">
                      <div class="text-center">
                        <div class="text-caption text-disabled">
                          {{ $t('history.containers.completed_date') }}
                        </div>
                        <div class="text-subtitle-2 text-success">
                          {{ formatDate(container.completed_date) }}
                        </div>
                      </div>
                    </VCol>
                  </VRow>
                    
                  <!-- Statistics -->
                  <VRow
                    dense
                    class="container-stats"
                  >
                    <VCol cols="6">
                      <div class="text-center">
                        <div class="text-h6 text-primary">
                          {{ container.parcels_count || 0 }}
                        </div>
                        <div class="text-caption">
                          {{ $t('history.containers.parcels_processed') }}
                        </div>
                      </div>
                    </VCol>
                    <VCol cols="6">
                      <div class="text-center">
                        <div class="text-h6 text-secondary">
                          {{ container.clients_count || 0 }}
                        </div>
                        <div class="text-caption">
                          {{ $t('history.containers.clients_served') }}
                        </div>
                      </div>
                    </VCol>
                    <VCol cols="6">
                      <div class="text-center">
                        <div class="text-subtitle-2 text-success">
                          {{ formatWeight(container.total_weight || 0) }}
                        </div>
                        <div class="text-caption">
                          {{ $t('history.containers.total_weight') }}
                        </div>
                      </div>
                    </VCol>
                    <VCol cols="6">
                      <div class="text-center">
                        <div class="text-subtitle-2 text-info">
                          {{ formatVolume(container.total_volume || 0) }}
                        </div>
                        <div class="text-caption">
                          {{ $t('history.containers.total_volume') }}
                        </div>
                      </div>
                    </VCol>
                  </VRow>
                    
                  <!-- Processing Time Progress -->
                  <div class="mt-3 processing-time-section">
                    <div class="d-flex justify-space-between align-center mb-2">
                      <span class="text-caption">{{ $t('history.containers.processing_time') }}</span>
                      <VChip
                        :color="getProcessingColor(container.processing_days)"
                        size="x-small"
                      >
                        {{ container.processing_days || 0 }} {{ $t('history.containers.days') }}
                      </VChip>
                    </div>
                    <VProgressLinear
                      :model-value="getProcessingScore(container.processing_days)"
                      :color="getProcessingColor(container.processing_days)"
                      height="4"
                      rounded
                    />
                  </div>
                </VCardText>
                  
                <VCardActions class="px-4 pb-3">
                  <VBtn
                    color="primary"
                    variant="text"
                    size="small"
                    prepend-icon="tabler-eye"
                    @click.prevent="$router.push(`/history/containers/${container.id}`)"
                  >
                    {{ $t('history.containers.view_details') }}
                  </VBtn>
                  <VSpacer />
                  <VBtn
                    color="secondary"
                    variant="text"
                    size="small"
                    icon="tabler-file-analytics"
                    @click.prevent="generateContainerReport(container)"
                  />
                </VCardActions>
              </VCard>
            </VCol>
          </VRow>
        </div>
        
        <!-- Pagination -->
        <div
          v-if="totalPages > 1"
          class="d-flex justify-center mt-6"
        >
          <VPagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="7"
            class="container-pagination"
            @update:model-value="fetchContainers"
          />
        </div>
      </VCardText>
    </VCard>
  </div>
</template>
  
<script setup>
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSnackbar } from '@/composables/useSnackbar'
import axios from 'axios'
  
definePage({
  meta: {
    requiresAuth: true,
    role: ['admin', 'manager', 'user'],
  },
})
  
const { t } = useI18n()
const { showSnackbar } = useSnackbar()
  
// Reactive data
const containers = ref([])
const isLoading = ref(false)
const searchQuery = ref('')
const selectedYear = ref('')
const selectedType = ref('')
const selectedOrigin = ref('')
const currentPage = ref(1)
const itemsPerPage = ref(8)
const totalItems = ref(0)
const viewMode = ref('table') // Default to table view
  
// Computed
const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value))

// Table headers
const tableHeaders = computed(() => [
  { title: t('history.containers.container_number'), key: 'container_number', sortable: true },
  { title: t('history.containers.parcels_processed'), key: 'parcels_count', sortable: true },
  { title: t('history.containers.clients_served'), key: 'clients_count', sortable: true },
  { title: t('history.containers.total_weight'), key: 'total_weight', sortable: true },
  { title: t('history.containers.total_volume'), key: 'total_volume', sortable: true },
  { title: t('history.containers.processing_time'), key: 'processing_days', sortable: true },
  { title: t('history.containers.arrived_date'), key: 'arrival_date', sortable: true },
  { title: t('history.containers.completed_date'), key: 'completed_date', sortable: true },
  { title: t('common.actions'), key: 'actions', sortable: false, width: '100' },
])
  
// Filter options
const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear()
  const years = [{ title: t('history.filters.all_time'), value: '' }]
    
  for (let year = currentYear; year >= currentYear - 5; year--) {
    years.push({ title: year.toString(), value: year.toString() })
  }
    
  return years
})
  
const typeOptions = computed(() => [
  { title: t('shipment.all'), value: '' },
  { title: t('shipment.by_plane'), value: 'air' },
  { title: t('shipment.by_ship'), value: 'sea' },
])
  
const originOptions = computed(() => [
  { title: t('shipment.all'), value: '' },
  { title: 'China', value: 'china' },
  { title: 'France', value: 'france' },
  { title: 'Germany', value: 'germany' },
  { title: 'Italy', value: 'italy' },
])
  
// Methods
const fetchContainers = async () => {
  try {
    isLoading.value = true
      
    const params = {
      page: currentPage.value,
      per_page: itemsPerPage.value,
      search: searchQuery.value,
      year: selectedYear.value,
      type: selectedType.value,
      origin: selectedOrigin.value,
      status: 'completed',
    }
      
    const response = await axios.get('/shipment/history/containers', { params })
      
    containers.value = response.data.data
    totalItems.value = response.data.total
  } catch (error) {
    console.error('Error fetching container history:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.fetch_failed', { item: 'container history' }),
    })
  } finally {
    isLoading.value = false
  }
}
  
const formatDate = date => {
  if (!date) return '-'

  return new Date(date).toLocaleDateString()
}
  
const formatWeight = weight => {
  if (weight) {
    return `${weight.toFixed(1)} kg`
  }
}

const formatVolume = volume => {
  if (volume) {
    return `${volume.toFixed(2)} m³`
  }
}
  
const getProcessingScore = days => {
  const maxDays = 30
  
  return Math.max(0, 100 - (days / maxDays) * 100)
}
  
const getProcessingColor = days => {
  if (days <= 5) return 'success'
  if (days <= 10) return 'warning'
  
  return 'error'
}
  
const generateContainerReport = async container => {
  try {
    const response = await axios.get(`/shipment/history/containers/${container.id}/report`, {
      responseType: 'blob',
    })
      
    const blob = new Blob([response.data], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.setAttribute('download', `container-report-${container.id}.pdf`)
    document.body.appendChild(link)
    link.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)
      
    showSnackbar({
      color: 'success',
      text: t('messages.report_generated'),
    })
  } catch (error) {
    console.error('Error generating container report:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.report_generation_failed'),
    })
  }
}
  
const exportData = async () => {
  try {
    const response = await axios.get('/shipment/history/containers/export', {
      params: {
        search: searchQuery.value,
        year: selectedYear.value,
        type: selectedType.value,
        origin: selectedOrigin.value,
      },
      responseType: 'blob',
    })
      
    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.setAttribute('download', 'container-history.xlsx')
    document.body.appendChild(link)
    link.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)
      
    showSnackbar({
      color: 'success',
      text: t('messages.export_successful'),
    })
  } catch (error) {
    console.error('Error exporting data:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.export_failed'),
    })
  }
}
  
// Lifecycle
onMounted(() => {
  fetchContainers()
})
</script>
  
<style scoped>
.container-history-main {
  font-family: 'Inter', 'Segoe UI', sans-serif;
}

.container-history-header {
  border: 1px solid rgb(var(--v-theme-surface-variant));
  background: rgb(var(--v-theme-surface));
}

.container-title {
  flex-grow: 1;
}

.view-toggle {
  border-radius: 6px;
}

.container-card {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  height: fit-content;
}

.container-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.container-dates {
  border: 1px solid rgb(var(--v-theme-surface-variant));
  border-radius: 6px;
  padding: 8px;
  background: rgb(var(--v-theme-surface-variant));
}

.container-stats {
  border-top: 1px solid rgb(var(--v-theme-surface-variant));
  margin-top: 12px;
  padding-top: 12px;
}

.processing-time-section {
  border-top: 1px solid rgb(var(--v-theme-surface-variant));
  padding-top: 8px;
}

.no-data-section {
  opacity: 0.7;
}

.container-filters {
  border-radius: 8px;
  padding: 16px;
  margin: -8px;
}

.container-history-table {
  border-radius: 8px;
}

.container-history-content {
  border: 1px solid rgb(var(--v-theme-surface-variant));
}

@media (max-width: 768px) {
  .view-toggle {
    order: 1;
    width: 100%;
    margin-bottom: 8px;
  }
  
  .export-containers-btn {
    order: 2;
    width: 100%;
  }
}
</style>